/**
 * OnboardingGate — enforces T&C acceptance + age verification before
 * a signed-in user can access protected app surfaces. Renders as a
 * modal-style overlay that cannot be dismissed until required steps
 * are complete. Under-13 users get a hard block screen (session revoked
 * server-side).
 *
 * Flow:
 *   1. Age gate (birthdate picker)
 *      - under 13   → hard block, session revoked
 *      - 13-15      → parental consent flow (email → parent SetupIntent link)
 *      - 16+        → straight to T&C step
 *   2. T&C + Privacy checkbox + accept
 *   3. Continue → protected app
 */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ShieldAlert, Check } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  return r.json();
}

export default function OnboardingGate({ children }) {
  const [status, setStatus] = useState(null); // { needs_terms, needs_age_verification, ... }
  const [step, setStep] = useState("loading"); // loading | age | parent | terms | blocked | done

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api("/terms/status");
        if (cancelled) return;
        setStatus(s);
        if (s.needs_age_verification) setStep("age");
        else if (s.needs_parent_consent) setStep("parent");
        else if (s.needs_terms || s.needs_privacy) setStep("terms");
        else setStep("done");
      } catch (e) {
        // Endpoint failure — fall open so we don't lock users out.
        setStep("done");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (step === "loading") {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian-900" data-testid="onboarding-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  if (step === "done") return children;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-obsidian-900/95 backdrop-blur-md px-6 py-10 overflow-y-auto" data-testid="onboarding-gate">
      <div className="w-full max-w-lg bg-obsidian-800 border border-obsidian-600 p-8 sm:p-10">
        {step === "age" && <AgeStep onDone={(bracket) => {
          if (bracket === "under_13") setStep("blocked");
          else if (bracket === "13_15") setStep("parent");
          else setStep("terms");
        }} />}
        {step === "parent" && <ParentStep onDone={() => setStep("terms")} />}
        {step === "terms" && <TermsStep versions={status} onDone={() => setStep("done")} />}
        {step === "blocked" && <BlockedStep />}
      </div>
    </div>
  );
}

// ---- Step 1: Age gate ----
function AgeStep({ onDone }) {
  const [birthdate, setBirthdate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!birthdate) {
      toast.error("Please enter your birthdate.");
      return;
    }
    try {
      setSubmitting(true);
      const r = await api("/age-gate/verify", {
        method: "POST",
        body: JSON.stringify({ birthdate }),
      });
      if (r.blocked) {
        onDone("under_13");
      } else if (r.requires_parent_consent) {
        onDone("13_15");
      } else {
        onDone(r.bracket);
      }
    } catch (e) {
      toast.error("Could not verify age. Please check the date and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="onboarding-age-step">
      <p className="font-sans uppercase tracking-[0.28em] text-[0.65rem] text-terracotta mb-4">
        Step 1 of 2 · Age verification
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 mb-3">
        A quick age check before we begin.
      </h2>
      <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
        Umbra journaling can surface heavy material. To keep this a safe space
        (and to comply with US child-privacy law), we ask everyone their age.
        Users 13–15 will need a parent's help; 16 and up can consent themselves.
      </p>
      <label htmlFor="birthdate" className="block font-sans uppercase tracking-[0.2em] text-[0.7rem] text-bone-400 mb-2">
        Your birthdate
      </label>
      <Input
        id="birthdate"
        type="date"
        value={birthdate}
        onChange={(e) => setBirthdate(e.target.value)}
        max={new Date().toISOString().slice(0, 10)}
        data-testid="onboarding-birthdate"
        className="bg-obsidian-900 border-obsidian-600 rounded-none py-5 font-sans text-bone-100"
      />
      <Button
        onClick={submit}
        disabled={!birthdate || submitting}
        data-testid="onboarding-age-continue"
        className="mt-6 w-full rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Continue
      </Button>
      <p className="mt-4 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 text-center">
        We never share this. See our{" "}
        <Link to="/privacy" className="underline text-bone-400 hover:text-terracotta">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}

// ---- Step 2a: Parental consent request (13-15 only) ----
function ParentStep({ onDone }) {
  const [parentEmail, setParentEmail] = useState("");
  const [parentName, setParentName] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!parentEmail) {
      toast.error("Enter your parent or guardian's email.");
      return;
    }
    try {
      setSubmitting(true);
      await api("/age-gate/parent-consent-request", {
        method: "POST",
        body: JSON.stringify({ parent_email: parentEmail, parent_name: parentName }),
      });
      setSent(true);
      toast.success("Consent request sent to your parent.");
    } catch (e) {
      toast.error("Could not send the consent email. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="onboarding-parent-step">
      <p className="font-sans uppercase tracking-[0.28em] text-[0.65rem] text-terracotta mb-4">
        Step 2 of 2 · Parental consent
      </p>
      {!sent ? (
        <>
          <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 mb-3">
            We need your parent's OK.
          </h2>
          <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
            Because you're between 13 and 15, US law requires us to get verified
            consent from a parent or guardian. Enter their email — they'll get
            a link, click through, and confirm with a small credit-card
            authorization (this is the FTC-approved method; no money is charged
            or held).
          </p>
          <label htmlFor="parent-name" className="block font-sans uppercase tracking-[0.2em] text-[0.7rem] text-bone-400 mb-2">
            Parent or guardian name
          </label>
          <Input
            id="parent-name"
            value={parentName}
            onChange={(e) => setParentName(e.target.value)}
            placeholder="Alex Johnson"
            data-testid="onboarding-parent-name"
            className="bg-obsidian-900 border-obsidian-600 rounded-none py-5 font-sans text-bone-100 mb-4"
          />
          <label htmlFor="parent-email" className="block font-sans uppercase tracking-[0.2em] text-[0.7rem] text-bone-400 mb-2">
            Parent or guardian email
          </label>
          <Input
            id="parent-email"
            type="email"
            value={parentEmail}
            onChange={(e) => setParentEmail(e.target.value)}
            placeholder="parent@example.com"
            data-testid="onboarding-parent-email"
            className="bg-obsidian-900 border-obsidian-600 rounded-none py-5 font-sans text-bone-100"
          />
          <Button
            onClick={submit}
            disabled={!parentEmail || submitting}
            data-testid="onboarding-parent-send"
            className="mt-6 w-full rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Email the consent request
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center gap-3 mb-6">
            <Check className="h-6 w-6 text-terracotta" />
            <h2 className="font-serif text-2xl text-bone-100">Consent request sent.</h2>
          </div>
          <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
            Your parent will receive an email at <strong>{parentEmail}</strong> with
            a verification link. Until they complete the flow, your account is
            limited to Chapter 1 (the free preview). We'll notify you here once
            they approve.
          </p>
          <Button
            onClick={onDone}
            data-testid="onboarding-parent-continue-limited"
            className="w-full rounded-none border border-bone-500 bg-transparent hover:border-terracotta hover:text-terracotta text-bone-100 font-sans uppercase tracking-wider text-xs py-6"
          >
            Continue with limited access
          </Button>
        </>
      )}
    </div>
  );
}

// ---- Step 3: Terms & Privacy acceptance ----
function TermsStep({ versions, onDone }) {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!accepted) {
      toast.error("Please tick the box to agree to the Terms.");
      return;
    }
    try {
      setSubmitting(true);
      await api("/terms/accept", {
        method: "POST",
        body: JSON.stringify({
          terms_version: versions.terms_version,
          privacy_version: versions.privacy_version,
          accepted_at_client: new Date().toISOString(),
        }),
      });
      toast.success("Welcome.");
      onDone();
    } catch (e) {
      toast.error("Could not save your consent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="onboarding-terms-step">
      <p className="font-sans uppercase tracking-[0.28em] text-[0.65rem] text-terracotta mb-4">
        One last step · Terms & Privacy
      </p>
      <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 mb-3">
        Before you enter the workbook…
      </h2>
      <p className="font-sans text-sm text-bone-400 leading-relaxed mb-6">
        Please review our{" "}
        <Link to="/terms" target="_blank" rel="noopener" className="underline text-terracotta hover:text-terracotta-deep">
          Terms of Service
        </Link>{" "}
        and{" "}
        <Link to="/privacy" target="_blank" rel="noopener" className="underline text-terracotta hover:text-terracotta-deep">
          Privacy Policy
        </Link>
        . Both are written in plain English. TL;DR: your journal is yours, we
        never sell your data, and we're not a medical service.
      </p>

      <div className="flex items-start gap-3 border border-obsidian-600 bg-obsidian-900/60 p-4 mb-6">
        <Checkbox
          id="terms-accept"
          checked={accepted}
          onCheckedChange={(v) => setAccepted(Boolean(v))}
          data-testid="onboarding-terms-checkbox"
          className="mt-0.5 border-bone-500 data-[state=checked]:bg-terracotta data-[state=checked]:border-terracotta"
        />
        <label
          htmlFor="terms-accept"
          className="font-sans text-sm text-bone-300 leading-relaxed cursor-pointer select-none"
        >
          I have read and agree to the{" "}
          <Link to="/terms" target="_blank" rel="noopener" className="underline text-terracotta">Terms of Service</Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" rel="noopener" className="underline text-terracotta">Privacy Policy</Link>
          . I confirm I meet the age requirements and understand that Umbra is not a medical service.
        </label>
      </div>

      <Button
        onClick={submit}
        disabled={!accepted || submitting}
        data-testid="onboarding-terms-accept"
        className="w-full rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6 disabled:opacity-40"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        Enter the workbook
      </Button>
    </div>
  );
}

// ---- Blocked (under 13) ----
function BlockedStep() {
  const goHome = () => {
    window.location.href = "/";
  };
  return (
    <div className="text-center" data-testid="onboarding-blocked">
      <ShieldAlert className="h-12 w-12 text-terracotta mx-auto mb-4" />
      <h2 className="font-serif text-3xl text-bone-100 mb-3">
        Umbra is for age 13 and up.
      </h2>
      <p className="font-sans text-sm text-bone-400 leading-relaxed mb-8">
        By law, we can't collect journal entries from anyone under 13.
        Your account has been closed and any information you shared has been
        scheduled for deletion. When you're older, we'll be here.
      </p>
      <Button
        onClick={goHome}
        data-testid="onboarding-blocked-home"
        className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs py-6 px-10"
      >
        Return home
      </Button>
    </div>
  );
}
